import { useMemo } from "react";
import {
  type CaptureActivity,
  type PendingMemoryBacklogSummary,
  type PendingMemorySummary,
  type SpokenMemorySessionSummary,
  useCaptureActivityQuery,
  usePendingMemoriesQuery,
  useSpokenMemorySessionsQuery,
} from "@/data/spoken-memory";

type SessionFeedSource = "capture" | "capture_pending";
const AMBIENT_CAPTURE_MODE = 1;
const EXPLICIT_CAPTURE_MODE = 2;
const EMPTY_CAPTURES: SpokenMemorySessionSummary[] = [];
const EMPTY_PENDING_MEMORIES: PendingMemorySummary[] = [];
const EMPTY_CAPTURE_ACTIVITY: CaptureActivity[] = [];

export interface SessionFeedRow {
  id: string;
  source: SessionFeedSource;
  title: string;
  subtitle?: string;
  startedAt: number;
  endedAt: number | null;
  sortAt: number;
  status: string;
  openable: boolean;
  // Display-only metadata. `pillKind` controls the orange/indigo dot on the
  // session row; `iconKind` controls the lock / check / briefcase icon.
  pillKind: "ambient" | "online" | "processing" | "attention";
  pillLabel?: string;
  iconKind:
    | "captured"
    | "locked"
    | "office"
    | "queued"
    | "processing"
    | "stale";
}

export interface AmbientBacklogFeedSummary {
  count: number;
  readyCount: number;
  processingCount: number;
  retryingCount: number;
  deviceCount: number;
  oldestStartedAt: number | null;
  newestStartedAt: number | null;
  latestActivityAt: number | null;
}

export function useSessionsFeed(workspaceSlug: string | null = null): {
  rows: SessionFeedRow[];
  captureActivity: CaptureActivity[];
  ambientBacklog: AmbientBacklogFeedSummary | null;
  loading: boolean;
  refresh: () => Promise<void>;
} {
  const sessionsQuery = useSpokenMemorySessionsQuery(workspaceSlug);
  const pendingQuery = usePendingMemoriesQuery(workspaceSlug);
  const activityQuery = useCaptureActivityQuery(workspaceSlug);

  const captures =
    sessionsQuery.data?.captures ??
    sessionsQuery.data?.sessions ??
    EMPTY_CAPTURES;
  const pendingMemories =
    pendingQuery.data?.pending_memories ?? EMPTY_PENDING_MEMORIES;
  const ambientBacklog = mapAmbientBacklog(
    pendingQuery.data?.pending_memory_backlog,
  );
  const captureActivity =
    activityQuery.data?.capture_activity ?? EMPTY_CAPTURE_ACTIVITY;

  const rows = useMemo(
    () => buildSessionFeedRows(captures, pendingMemories),
    [captures, pendingMemories],
  );

  return {
    rows,
    captureActivity,
    ambientBacklog,
    loading:
      Boolean(workspaceSlug) &&
      (sessionsQuery.isPending ||
        pendingQuery.isPending ||
        activityQuery.isPending),
    refresh: async () => {
      await Promise.all([
        sessionsQuery.refetch(),
        pendingQuery.refetch(),
        activityQuery.refetch(),
      ]);
    },
  };
}

export function buildSessionFeedRows(
  captures: SpokenMemorySessionSummary[],
  pendingMemories: PendingMemorySummary[],
): SessionFeedRow[] {
  return [
    ...captures.filter(isVisibleCaptureRow).map(captureToFeedRow),
    ...pendingMemories
      .filter((memory) => memory.mode === EXPLICIT_CAPTURE_MODE)
      .map(pendingCaptureToFeedRow),
  ].sort(compareFeedRowsReverseChronological);
}

function captureToFeedRow(s: SpokenMemorySessionSummary): SessionFeedRow {
  return {
    id: s.id,
    source: "capture",
    title: s.title || "Untitled capture",
    startedAt: s.started_at,
    endedAt: s.ended_at ?? null,
    sortAt: s.started_at,
    status: s.status,
    openable: true,
    pillKind: "ambient",
    pillLabel: s.mode === EXPLICIT_CAPTURE_MODE ? "manual" : "ambient",
    iconKind:
      s.status === "processing"
        ? "processing"
        : s.status === "ready"
          ? "captured"
          : s.status === "locked"
            ? "locked"
            : "captured",
  };
}

function pendingCaptureToFeedRow(memory: PendingMemorySummary): SessionFeedRow {
  const deviceName = memory.device_name ?? "capture device";
  const title =
    memory.status === "failed"
      ? "Recording needs attention"
      : memory.status === "processing"
        ? "Transcribing recording"
        : "Preparing recording";
  const statusLabel =
    memory.status === "failed"
      ? "needs attention"
      : memory.status === "processing"
        ? "transcribing"
        : "queued";
  return {
    id: memory.id,
    source: "capture_pending",
    title,
    subtitle: `${deviceName} · ${formatWindowRange(memory.started_at, memory.ended_at)}`,
    startedAt: memory.started_at,
    endedAt: memory.ended_at,
    sortAt: memory.started_at,
    status: memory.status,
    openable: false,
    pillKind: memory.status === "failed" ? "attention" : "processing",
    pillLabel: statusLabel,
    iconKind:
      memory.status === "failed"
        ? "stale"
        : memory.status === "processing"
          ? "processing"
          : "queued",
  };
}

function compareFeedRowsReverseChronological(
  a: SessionFeedRow,
  b: SessionFeedRow,
): number {
  const startedDelta = b.startedAt - a.startedAt;
  if (startedDelta !== 0) return startedDelta;

  const endedDelta = (b.endedAt ?? 0) - (a.endedAt ?? 0);
  if (endedDelta !== 0) return endedDelta;

  return a.id.localeCompare(b.id);
}

function isVisibleCaptureRow(session: SpokenMemorySessionSummary): boolean {
  if (session.mode === EXPLICIT_CAPTURE_MODE) return true;
  if (session.mode !== AMBIENT_CAPTURE_MODE) return true;
  return session.block_count > 0 || session.segment_count > 0;
}

function mapAmbientBacklog(
  backlog?: PendingMemoryBacklogSummary | null,
): AmbientBacklogFeedSummary | null {
  if (!backlog || backlog.ambient_count <= 0) return null;
  return {
    count: backlog.ambient_count,
    readyCount: backlog.ambient_ready_count,
    processingCount: backlog.ambient_processing_count,
    retryingCount: backlog.ambient_retrying_count,
    deviceCount: backlog.ambient_device_count,
    oldestStartedAt: backlog.ambient_oldest_started_at,
    newestStartedAt: backlog.ambient_newest_started_at,
    latestActivityAt: backlog.ambient_latest_activity_at,
  };
}

function formatWindowRange(startedAt: number, endedAt: number): string {
  return `${formatTimeOfDay(startedAt)} - ${formatTimeOfDay(endedAt)}`;
}

// "Today", "Yesterday", or "Fri, May 8" — used to group rows on Home.
export function feedDateLabel(tsMs: number, now: number = Date.now()): string {
  const day = new Date(tsMs);
  const today = new Date(now);
  day.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.round(
    (today.getTime() - day.getTime()) / (24 * 60 * 60 * 1000),
  );
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  return day.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

export function formatTimeOfDay(tsMs: number): string {
  return new Date(tsMs).toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}
